import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { eventViewOf } from "utils/mediaEditorView";
import api from "../utils/axios";

// ─── Query Keys ───
const EVENTS_KEY = ["events"];
const EVENT_PROPERTIES_KEY = ["event-properties"];
const ADMIN_CONTRACTOR_EVENTS_KEY = ["adminContractorEvents"];

// ─── Queries ───

/** Fetch all calendar events */
export const useEventsQuery = () => {
  return useQuery({
    queryKey: EVENTS_KEY,
    queryFn: async () => {
      const { data } = await api.get("/admin/events");
      return data.data.map((event) => {
        const isDatabaseEvent = event.summary !== "Personal Event";
        return {
          ...event,
          title: event.name || event.summary,
          start: isDatabaseEvent
            ? new Date(event.start)
            : new Date(event.start.dateTime || event.start.date),
          end: isDatabaseEvent
            ? new Date(event.end)
            : new Date(event.end.dateTime || event.end.date),
        };
      });
    },
  });
};

/** Fetch events by property ID */
export const useGetEventsByPropertyId = (propertyId) => {
  return useQuery({
    queryKey: ["events", propertyId],
    queryFn: async () => {
      const { data } = await api.get(`/admin/events/propertyId/${propertyId}`);
      return data.data;
    },
    enabled: !!propertyId,
  });
};

/** Fetch unique properties that have events */
export const useEventPropertiesQuery = () => {
  return useQuery({
    queryKey: EVENT_PROPERTIES_KEY,
    queryFn: async () => {
      const { data } = await api.get("/admin/events/uniqueProperties");
      return data.data;
    },
  });
};

/** Fetch admin contractor events by propertyId */
export const useAdminContractorEventsQuery = (propertyId) => {
  return useQuery({
    queryKey: [...ADMIN_CONTRACTOR_EVENTS_KEY, propertyId],
    queryFn: async () => {
      const res = await api.get(`/admin/prepareMarketing/events`, {
        params: { propertyId },
      });
      // A rental job's files come as Media rows (event.media); the job
      // screens read their view (utils/mediaEditorView.js).
      const categories = res.data?.data || {};
      return Object.fromEntries(
        Object.entries(categories).map(([category, list]) => [
          category,
          Array.isArray(list) ? list.map(eventViewOf) : list,
        ]),
      );
    },
    enabled: !!propertyId,
    staleTime: 2 * 60 * 1000,
  });
};

// ─── Mutations ───

/** Create a new event */
export const useAddEventMutation = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (eventData) => {
      const { data } = await api.post("/admin/events", eventData);
      return data.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: EVENTS_KEY });
      queryClient.invalidateQueries({ queryKey: EVENT_PROPERTIES_KEY });
    },
  });
};

/** Update an existing event */
export const useUpdateEventMutation = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (eventData) => {
      const { data } = await api.put("/admin/events", eventData);
      return data.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: EVENTS_KEY });
    },
  });
};

/** Delete an event */
export const useDeleteEventMutation = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (eventId) => {
      const { data } = await api.delete(`/admin/events/${eventId}`);
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: EVENTS_KEY });
      queryClient.invalidateQueries({ queryKey: EVENT_PROPERTIES_KEY });
    },
  });
};

export const useEventLogsQuery = ({ propertyId, changeType, page = 1 } = {}) => {
  return useQuery({
    queryKey: ["event-logs", propertyId, changeType, page],
    queryFn: async () => {
      const { data } = await api.get("/admin/events/logs", {
        params: { propertyId, changeType, page, limit: 50 },
      });
      return data;
    },
    keepPreviousData: true, // ← keeps old page on screen while next page loads
  });
};